<script lang="ts">
  import CurrentUserStore from "@shared/svelte/Stores/CurrentUserStore";
  import Icon from "@shared/svelte/Components/Icon.svelte";
  import LayoutStore from "@shared/svelte/Stores/LayoutStore";
  import { ProductSortingEntity } from "@shared/services/event_tracking/entities";
  import { EventTracking } from "@shared/services/event_tracking/events";
  import { ProductLinesSortOptions } from "@shared/types/ProductLinesSortOptions";
  import SortSelect from "@shared/svelte/Components/SortSelect.svelte";

  const { trackClick } = EventTracking;

  export let refreshState;
  export let stateParams;
  export let isPriceSorted: boolean | null = null;

  const defaultSort = ProductLinesSortOptions.BestSellers;

  const initialSortValue = (stateParams ? stateParams.product_sort : defaultSort) || defaultSort;

  // limited ordering users don't see prices, so can't sort by price
  const allowSortByPrice = !$CurrentUserStore.limited_ordering;

  $: productSortValue = (stateParams ? stateParams.product_sort : defaultSort) || defaultSort;
  $: productSortText = options.find(o => o.value == productSortValue).text;
  $: isPriceSorted = productSortValue.startsWith("price");

  const options = [];

  options.push({ text: "Best sellers", value: ProductLinesSortOptions.BestSellers });

  if (allowSortByPrice) {
    options.push({ text: "Price: Low to High", value: ProductLinesSortOptions.PriceAsc });
    options.push({ text: "Price: High to Low", value: ProductLinesSortOptions.PriceDesc });
  }

  options.push({ text: "Newly added", value: ProductLinesSortOptions.NewlyAdded });
  options.push({ text: "Product name: A→Z", value: ProductLinesSortOptions.NameAsc });
  options.push({ text: "Product name: Z→A", value: ProductLinesSortOptions.NameDesc });

  const onChange = e => {
    const res = e.target.value;
    applyChange(res);
  };

  const applyChange = change => {
    trackClick(null, new ProductSortingEntity({ value: change === "" ? "bestsellers" : change }));

    const newStateParams = { ...stateParams };
    if (change !== "") {
      newStateParams.product_sort = change;
    } else {
      delete newStateParams["product_sort"];
    }

    sortByPanelOpened = false;
    refreshState(newStateParams);
  };

  const updateSelection = newSelection => {
    stateParams = { ...stateParams, product_sort: newSelection.value };
  };

  const onSeeResults = () => {
    applyChange(productSortValue);
  };

  let sortByPanelOpened: boolean;
  const openSortPanel = () => {
    sortByPanelOpened = true;
  };

  const closeSortPanel = () => {
    sortByPanelOpened = false;

    const updatedStateParams = { ...stateParams };
    if (initialSortValue !== "") {
      updatedStateParams.product_sort = initialSortValue;
    }

    stateParams = updatedStateParams;
  };
</script>

{#if $LayoutStore.isMobile}
  <button
    on:click={openSortPanel}
    class="fizz-button fizz-full-width"
    id="mobile-sort-by-button"
    data-testid="mobile-sort-by-button"
    style="margin-right: 0;"
  >
    <div class="fizz-flex-row-8 fizz-justify-center">
      <span class="fizz-text-regular">Sort by: </span>
      <span class="fizz-text-strong">{productSortText}</span>
      <Icon type="ArrowDown" width="8" height="8" />
    </div>
  </button>

  <div class="fizz-has-panel" class:fizz-panel-is-open={sortByPanelOpened}>
    <div
      class="fizz-panel"
      style="--panel-width: 100%; height: 100%; position: fixed; z-index: 20000;"
    >
      <div class="fizz-border-top fizz-border-bottom fizz-inset-square-16">
        <div class="fizz-flex-row fizz-space-between">
          <span class="fizz-heading-4">Sort by</span>
          <button data-testid="close-sorting-select" on:click={closeSortPanel}>
            <Icon width="16" height="16" type="X" />
          </button>
        </div>
      </div>
      <div class="fizz-inset-square-16" style="height: 70vh;" id="mobile-product-sort-select">
        <ul class="fizz-flow-4">
          {#each options as option}
            <li>
              <label class="fizz-flex-row-8">
                <input
                  id="sorting-select-{option.value}"
                  type="radio"
                  class="fizz-radio-button"
                  value={option.value}
                  checked={option.value == productSortValue}
                  on:change={() => updateSelection(option)}
                />
                <span>{option.text}</span>
              </label>
            </li>
          {/each}
        </ul>
      </div>
      <div class="fizz-border-top fizz-inset-square-16">
        <button
          data-testid="apply-sort-by"
          type="button"
          class="fizz-button-secondary fizz-full-width"
          on:click={onSeeResults}
        >
          See results
        </button>
      </div>
    </div>
  </div>
{:else}
  <SortSelect {options} bind:selectedOptionValue={productSortValue} {onChange} />
{/if}
